import { useState } from 'react';
import { css } from '@emotion/react';
import { IconButton, Typography } from '@mui/material';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { colors } from '../../../../../theme/colors';
import { BasicDialog } from '../../../../../components/Dialog/BasicDialog';
import { TEARSHEET_PANELS } from '../constants';
import { PANEL_CONFIG_COMPONENTS } from '../panelConfigMap';
import { DropTarget, LayoutPanelCell } from '../types';
import { DropZoneOverlay } from './DropZoneOverlay';

const panelSlotContainerStyle = css`
  width: 100%;
  height: 100%;
  position: relative;
`;

const panelSlotStyle = css`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: ${colors.neutral[5]};
  border: 1px solid ${colors.neutral[20]};
  position: relative;
  transition: background 0.15s ease;

  &:hover {
    background: ${colors.neutral[10]};
  }
`;

const panelActionsStyle = css`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  display: flex;
  gap: 0.25rem;
  opacity: 0;
  transition: opacity 0.15s ease;
`;

const showControlsOnHoverStyle = css`
  &:hover .panel-controls {
    opacity: 1;
  }
`;

const actionButtonStyle = css`
  background: white;
  border: 1px solid ${colors.neutral[20]};
  padding: 0.25rem;

  &:hover {
    background: ${colors.neutral[10]};
  }
`;

const dragHandleStyle = css`
  position: absolute;
  top: 0.5rem;
  left: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  background: white;
  border: 1px solid ${colors.neutral[20]};
  border-radius: 4px;
  cursor: grab;
  opacity: 0;
  transition: opacity 0.15s ease;
  touch-action: none;

  &:active {
    cursor: grabbing;
  }

  &:hover {
    background: ${colors.neutral[10]};
  }
`;

const panelSlotContainerDraggingStyle = css`
  opacity: 0.5;
`;

const modalContentStyle = css`
  padding: 0.5rem;
`;

interface PanelSlotProps {
  cell: LayoutPanelCell;
  activeDropTarget?: DropTarget | null;
  onRemove?: () => void;
}

export function PanelSlot({ cell, activeDropTarget, onRemove }: PanelSlotProps) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const panelMeta = TEARSHEET_PANELS[cell.panelType];
  const ConfigComponent = PANEL_CONFIG_COMPONENTS[cell.panelType];

  // Make this panel draggable
  const {
    attributes: dragAttributes,
    listeners: dragListeners,
    setNodeRef: setDragNodeRef,
    isDragging: isThisDragging,
  } = useDraggable({
    id: `layout-panel-${cell.id}`,
    data: {
      type: 'layout-panel',
      cellId: cell.id,
      panelType: cell.panelType,
    },
  });

  // Make this panel droppable
  const { setNodeRef: setDropNodeRef, isOver } = useDroppable({
    id: cell.id,
    data: {
      type: 'panel-slot',
      cellId: cell.id,
      panelType: cell.panelType,
    },
  });

  // Check if this panel is the active drop target
  const isActiveTarget = activeDropTarget?.type === 'panel' && activeDropTarget?.cellId === cell.id;
  const currentZone = isActiveTarget ? activeDropTarget.zone : null;

  // Show drop zone overlay when dragging over this panel or when this is the active target
  const showDropZones = isOver || isActiveTarget;

  return (
    <div
      css={[
        panelSlotContainerStyle,
        showControlsOnHoverStyle,
        isThisDragging && panelSlotContainerDraggingStyle,
      ]}
      ref={setDropNodeRef}
      data-panel-id={cell.id}
    >
      <div css={panelSlotStyle}>
        {/* Drag handle */}
        <div
          ref={setDragNodeRef}
          {...dragListeners}
          {...dragAttributes}
          css={dragHandleStyle}
          className='panel-controls'
          title='Drag to move panel'
        >
          <DragIndicatorIcon fontSize='small' />
        </div>

        <Typography variant='body2' fontWeight={500}>
          {panelMeta?.label ?? 'Unknown panel'}
        </Typography>
        <Typography variant='caption' color='text.secondary'>
          {panelMeta?.description}
        </Typography>

        <div css={panelActionsStyle} className='panel-controls'>
          {ConfigComponent && (
            <IconButton
              css={actionButtonStyle}
              size='small'
              onClick={() => setSettingsOpen(true)}
              title='Configure panel'
            >
              <SettingsIcon fontSize='small' />
            </IconButton>
          )}
          {onRemove && (
            <IconButton css={actionButtonStyle} size='small' onClick={onRemove} title='Remove panel'>
              <CloseIcon fontSize='small' />
            </IconButton>
          )}
        </div>
      </div>

      <DropZoneOverlay activeZone={showDropZones ? currentZone : null} />

      {ConfigComponent && (
        <BasicDialog
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          maxWidth='md'
          fullWidth
          title={panelMeta?.label ?? cell.panelType}
          lightDismiss
        >
          <div css={modalContentStyle}>
            <ConfigComponent isInModal />
          </div>
        </BasicDialog>
      )}
    </div>
  );
}
